import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { testProviderWithCooldown, getCooldownUntil } from "../lib/testCooldown";
import { Facebook, Database, CheckCircle2, XCircle, Loader2, ExternalLink, Lock, Zap, Activity, AlertTriangle, Bell, KeyRound, Mail, Rocket, Users } from "lucide-react";
import HelpBubble from "../components/HelpBubble";
import { useAuth } from "../context/AuthContext";

export default function Connections() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Hydrate from the last known-good snapshot so remounts (router nav back)
  // don't flash "Connect now" while /connections/status is in flight.
  const snap = (() => {
    try { return JSON.parse(sessionStorage.getItem("rsoc.connStatus") || "null"); } catch { return null; }
  })();
  const [status, setStatus] = useState(snap || { facebook: false, system1: false, fb_ad_accounts: [], s1paid: null });
  const [statusLoaded, setStatusLoaded] = useState(!!snap);
  const [statusError, setStatusError] = useState(null);
  const [statusLoadedAt, setStatusLoadedAt] = useState(null);
  const [rowBusy, setRowBusy] = useState({});
  // Auto-refresh interval (ms). 0 = off. Persisted per-browser.
  const [autoRefreshMs, setAutoRefreshMs] = useState(() => {
    try { return Number(localStorage.getItem("rsoc.connAutoRefresh") || "0") || 0; } catch { return 0; }
  });
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const [fbToken, setFbToken] = useState("");
  const [pixel, setPixel] = useState({ pixel_id: "", capi_token: "", test_event_code: "" });
  const [postbackUrl, setPostbackUrl] = useState("");
  const [postbackHealth, setPostbackHealth] = useState(null);
  const [falKey, setFalKey] = useState("");
  const [falEnabled, setFalEnabled] = useState(false);
  const [openaiKey, setOpenaiKey] = useState("");
  const [soraSource, setSoraSource] = useState(null);
  const [elevenKey, setElevenKey] = useState("");
  const [voEnabled, setVoEnabled] = useState(false);
  const [emergentKey, setEmergentKey] = useState("");
  const [resendKey, setResendKey] = useState("");
  const [keysStatus, setKeysStatus] = useState({});
  const [testResults, setTestResults] = useState({}); // {provider: {ok,latency_ms,detail,error}}
  const [busy, setBusy] = useState({ fb: false, pixel: false, fal: false, openai: false, eleven: false, emergent: false, resend: false, test: null });

  const testKey = async (provider) => {
    setBusy((b) => ({ ...b, test: provider }));
    const data = await testProviderWithCooldown(provider);
    if (data) setTestResults((r) => ({ ...r, [provider]: data }));
    setBusy((b) => ({ ...b, test: null }));
  };

  const load = async () => {
    let statusData = null;
    try {
      const { data } = await api.get("/connections/status", { timeout: 15000 });
      statusData = data;
      setStatus(data);
      setStatusError(null);
      setStatusLoadedAt(Date.now());
      try { sessionStorage.setItem("rsoc.connStatus", JSON.stringify(data)); } catch {}
    } catch (e) {
      // Don't wipe the last-known-good status — show a retry banner instead so
      // the UI doesn't deceptively revert to "Connect now" on a transient fail.
      const msg = e?.code === "ECONNABORTED"
        ? "Request timed out after 15s — server may be slow"
        : (e?.response?.data?.detail || e?.message || "Failed to load connections");
      setStatusError(msg);
    } finally {
      setStatusLoaded(true);
    }
    // Remaining calls are best-effort; don't block status display if they fail.
    try {
      const { data: pb } = await api.get("/postback/url");
      setPostbackUrl(pb.postback_url_template);
    } catch {}
    try {
      const { data: ph } = await api.get("/postback/health");
      setPostbackHealth(ph);
    } catch {}
    if (user?.role === "admin") {
      try {
        const { data: lh } = await api.get("/llm/health");
        setFalEnabled(!!lh?.video?.fal_ugc_enabled);
        setSoraSource(lh?.video?.sora_source || null);
        setVoEnabled(!!lh?.video?.voiceover_enabled);
        setKeysStatus(lh?.keys || {});
      } catch (e) { console.error("llm health:", e); }
    }
    return statusData;
  };
  useEffect(() => {
    load();
    // Re-fetch status whenever the tab regains focus (covers: save keys on
    // Team page → switch back here → pills update without a manual refresh)
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    const onVisibility = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // Auto-refresh: when a non-zero interval is chosen, poll /connections/status
  // on a timer. Persist choice so buyers keep their preferred cadence.
  useEffect(() => {
    try { localStorage.setItem("rsoc.connAutoRefresh", String(autoRefreshMs)); } catch {}
    if (!autoRefreshMs) return undefined;
    const id = setInterval(() => { load(); }, autoRefreshMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefreshMs]);

  const saveFalKey = async () => {
    if (!falKey.trim()) return toast.error("fal.ai API key required");
    setBusy((b) => ({ ...b, fal: true }));
    try {
      await api.post("/connections/fal", { fal_key: falKey.trim() });
      toast.success("fal.ai key saved — UGC video tier unlocked");
      setFalKey("");
      setFalEnabled(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy((b) => ({ ...b, fal: false }));
    }
  };

  const saveOpenaiKey = async () => {
    if (!openaiKey.trim()) return toast.error("OpenAI API key required");
    setBusy((b) => ({ ...b, openai: true }));
    try {
      const { data } = await api.post("/connections/openai", { openai_key: openaiKey.trim() });
      toast.success("OpenAI key saved — Sora 2 now routed direct");
      setOpenaiKey("");
      setSoraSource(data?.sora_source || "direct_openai");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy((b) => ({ ...b, openai: false }));
    }
  };

  const saveElevenKey = async () => {
    if (!elevenKey.trim()) return toast.error("ElevenLabs API key required");
    setBusy((b) => ({ ...b, eleven: true }));
    try {
      await api.post("/connections/elevenlabs", { elevenlabs_key: elevenKey.trim() });
      toast.success("ElevenLabs saved — Tier 4 voiceover unlocked");
      setElevenKey("");
      setVoEnabled(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy((b) => ({ ...b, eleven: false }));
    }
  };

  const saveEmergentKey = async () => {
    if (!emergentKey.trim()) return toast.error("Emergent key required");
    setBusy((b) => ({ ...b, emergent: true }));
    try {
      await api.post("/connections/emergent", { emergent_key: emergentKey.trim() });
      toast.success("Emergent Universal Key saved — Claude / Gemini / Sora unlocked");
      setEmergentKey("");
      setKeysStatus((k) => ({ ...k, emergent_llm_set: true }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy((b) => ({ ...b, emergent: false }));
    }
  };

  const saveResendKey = async () => {
    if (!resendKey.trim()) return toast.error("Resend API key required");
    setBusy((b) => ({ ...b, resend: true }));
    try {
      await api.post("/connections/resend", { resend_key: resendKey.trim() });
      toast.success("Resend key saved — digest & password-reset emails enabled");
      setResendKey("");
      setKeysStatus((k) => ({ ...k, resend_set: true }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy((b) => ({ ...b, resend: false }));
    }
  };

  const savePixel = async () => {
    if (!pixel.pixel_id || !pixel.capi_token) return toast.error("Pixel ID + CAPI token required");
    setBusy((b) => ({ ...b, pixel: true }));
    try {
      await api.post("/connections/facebook/pixel", pixel);
      toast.success("Pixel + CAPI token saved");
      setPixel({ pixel_id: "", capi_token: "", test_event_code: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy((b) => ({ ...b, pixel: false })); }
  };

  const connectFB = async () => {
    if (!fbToken) return toast.error("Token required");
    setBusy((b) => ({ ...b, fb: true }));
    try {
      const { data } = await api.post("/connections/facebook", { access_token: fbToken });
      toast.success(`${data.ad_accounts.length} ad accounts connected`);
      setFbToken("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "FB connect failed");
    } finally { setBusy((b) => ({ ...b, fb: false })); }
  };

  const disconnect = async () => {
    await api.delete(`/connections/facebook`);
    try { sessionStorage.removeItem("rsoc.connStatus"); } catch {}
    toast.success("Facebook disconnected");
    load();
  };

  const StatusPill = ({ ok, label, loading }) => (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-0.5 rounded border ${
        loading ? "bg-slate-100 border-slate-200 text-slate-500"
          : ok ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-600"
               : "bg-zinc-800/50 border-zinc-700 text-slate-500"
      }`}
      data-testid={`status-${label}`}
    >
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {loading ? "checking…" : ok ? "connected" : "not connected"}
    </span>
  );

  // Scope badge on every connection card so buyers never wonder why a teammate's
  // key is "missing" — it wasn't missing, it's just scoped differently.
  //   scope="user" — private to THIS user only (FB token, FB Pixel). Teammates
  //                  have their own, invisible to you.
  //   scope="server" — shared default for the whole server. An admin can set
  //                    a workspace-specific override on the Team page which
  //                    takes precedence for THIS workspace only.
  const ScopeBadge = ({ scope, testid }) => {
    const cfg = scope === "user"
      ? { label: "Yours only", cls: "bg-slate-100 text-slate-600 border-slate-200", tip: "Private to your login · teammates connect their own Facebook separately" }
      : { label: "Server default", cls: "bg-blue-50 text-blue-700 border-blue-200", tip: "Shared default for every workspace on this server. Admins can set a workspace-specific override on the Team page that takes priority." };
    return (
      <span
        data-testid={testid || `scope-${scope}`}
        title={cfg.tip}
        className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border ${cfg.cls}`}
      >
        {scope === "user" ? <Lock className="w-2.5 h-2.5" /> : <Users className="w-2.5 h-2.5" />}
        {cfg.label}
      </span>
    );
  };

  const renderTestBtn = (provider, keyConfigured) => {
    const r = testResults[provider];
    const testing = busy.test === provider;
    const cooldownUntil = getCooldownUntil(provider);
    const secsLeft = cooldownUntil ? Math.max(0, Math.ceil((cooldownUntil - now) / 1000)) : 0;
    const onCooldown = secsLeft > 0;
    return (
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => testKey(provider)}
          disabled={!keyConfigured || testing || onCooldown}
          data-testid={`test-${provider}-btn`}
          className="text-[10px] font-medium px-2 py-0.5 bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 rounded disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
          title={
            !keyConfigured ? "Save a key first"
            : onCooldown ? `Cooldown — wait ${secsLeft}s (prevents self-inflicted rate-limits)`
            : "Live-ping this provider to confirm the key works"
          }
        >
          {testing ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : null}
          {onCooldown ? `Test (${secsLeft}s)` : "Test"}
        </button>
        {r && (
          <span
            data-testid={`test-${provider}-result`}
            className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${
              r.ok
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-red-50 text-red-700 border-red-200"
            }`}
            title={r.ok ? r.detail : (r.error || r.hint || "")}
          >
            {r.ok ? `✓ ${r.latency_ms}ms` : "✗ failed"}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 max-w-[1000px]" data-testid="connections-page" style={{ fontFamily: "Manrope" }}>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">My Facebook <HelpBubble feature="connect" /></h1>
          <p className="text-xs text-slate-500 mt-1 font-mono">
            Your personal Facebook Ads connection. Only you can see your FB campaigns.
          </p>
        </div>
        <PreflightWizard status={status} isAdmin={user?.role === "admin"} />
      </div>

      {/* Global sync indicator so users always know when we last pinged the server */}
      <div className="flex items-center justify-end gap-2 mb-3 text-[11px] text-slate-500 font-mono" data-testid="conn-refresh-bar">
        {statusLoaded
          ? <span data-testid="last-checked">Last checked: {statusLoadedAt ? new Date(statusLoadedAt).toLocaleTimeString() : "just now"}</span>
          : <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Checking connections…</span>}
        <select
          value={autoRefreshMs}
          onChange={(e) => setAutoRefreshMs(Number(e.target.value))}
          data-testid="conn-auto-refresh-select"
          title="Auto-refresh connection status"
          className="bg-white border border-slate-200 rounded px-1.5 py-0.5 text-[11px] font-mono"
        >
          <option value="0">Auto: off</option>
          <option value="60000">Auto: 1 min</option>
          <option value="300000">Auto: 5 min</option>
          <option value="900000">Auto: 15 min</option>
        </select>
        <button
          onClick={() => { setStatusLoaded(false); load(); }}
          disabled={!statusLoaded}
          data-testid="conn-refresh-btn"
          className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50"
        >
          <Activity className="w-3 h-3" /> Refresh
        </button>
      </div>

      <div className="space-y-4">
        {/* Facebook (per-user) */}
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-blue-600/20 border border-blue-500/30 rounded-md flex items-center justify-center">
                <Facebook className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <div className="text-sm font-medium flex items-center gap-1.5">Facebook Ads <ScopeBadge scope="user" testid="scope-fb" /></div>
                <div className="text-[11px] text-slate-500 font-mono">Buyside · Graph API v21 · private to you</div>
              </div>
            </div>
            <StatusPill ok={status.facebook} label="facebook" loading={!statusLoaded || !!statusError} />
          </div>

          {!statusLoaded ? (
            <div className="flex items-center gap-2 text-xs text-slate-500 py-3" data-testid="fb-status-loading">
              <Loader2 className="w-3 h-3 animate-spin" /> Checking Facebook connection…
            </div>
          ) : statusError && !status.facebook ? (
            <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-[12px] text-amber-800 space-y-2" data-testid="fb-status-error">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                <span>Couldn't verify connection: {statusError}. Your stored token is <strong>still saved</strong> — this is a transient load failure.</span>
              </div>
              <button onClick={load} data-testid="fb-status-retry" className="text-[11px] font-medium px-2 py-1 bg-white border border-amber-300 rounded hover:bg-amber-100">
                Retry
              </button>
            </div>
          ) : status.facebook ? (
            <div className="space-y-2">
              <div className="text-xs text-slate-500">Ad accounts:</div>
              <div className="flex flex-wrap gap-1.5">
                {status.fb_ad_accounts.map((a) => {
                  const sMap = {
                    1: { label: "Active",   cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
                    2: { label: "Disabled", cls: "bg-red-50 text-red-700 border-red-200" },
                    3: { label: "Unsettled", cls: "bg-amber-50 text-amber-700 border-amber-200" },
                    7: { label: "Risk Review", cls: "bg-amber-50 text-amber-700 border-amber-200" },
                    8: { label: "Pending Settlement", cls: "bg-amber-50 text-amber-700 border-amber-200" },
                    9: { label: "Grace Period", cls: "bg-amber-50 text-amber-700 border-amber-200" },
                    100: { label: "Pending Closure", cls: "bg-red-50 text-red-700 border-red-200" },
                    101: { label: "Closed",  cls: "bg-red-50 text-red-700 border-red-200" },
                    102: { label: "Pending Settlement", cls: "bg-amber-50 text-amber-700 border-amber-200" },
                  };
                  const st = sMap[a.status] || { label: `Status ${a.status}`, cls: "bg-slate-100 text-slate-700 border-slate-200" };
                  return (
                    <span
                      key={a.id}
                      data-testid={`fb-acct-${a.id}`}
                      className="inline-flex items-center gap-1.5 text-[11px] font-mono bg-white border border-slate-200 rounded px-2 py-1"
                    >
                      <span className={`px-1.5 py-0.5 rounded border ${st.cls}`} data-testid={`fb-acct-status-${a.id}`}>
                        {st.label}
                      </span>
                      {a.name} · {a.currency} · <span className="text-slate-500">{a.id}</span>
                    </span>
                  );
                })}
              </div>
              {status.fb_ad_accounts.length > 0 && status.fb_ad_accounts.every((a) => a.status !== 1) && (
                <div
                  data-testid="fb-no-active-warning"
                  className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5"
                >
                  ⚠ None of your linked ad accounts are Active — campaigns pushed from this hub will not spend. Add an active ad account to your System User in Business Manager, then re-paste your token.
                </div>
              )}
              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={async () => {
                    try {
                      const { data } = await api.get("/connections/facebook/debug");
                      // Render diagnostic JSON in a new tab via a sandboxed Blob URL
                      // (document.write is deprecated + a known XSS vector). The JSON
                      // is also HTML-escaped defensively.
                      const safe = JSON.stringify(data, null, 2)
                        .replace(/&/g, "&amp;")
                        .replace(/</g, "&lt;")
                        .replace(/>/g, "&gt;");
                      const html =
                        `<!doctype html><meta charset="utf-8">` +
                        `<title>FB token debug</title>` +
                        `<pre style="font:12px monospace;padding:20px;white-space:pre-wrap">${safe}</pre>`;
                      const blob = new Blob([html], { type: "text/html" });
                      const url = URL.createObjectURL(blob);
                      const w = window.open(url, "_blank", "noopener,noreferrer");
                      // Clean up the blob URL after the tab loads it.
                      if (w) setTimeout(() => URL.revokeObjectURL(url), 60_000);
                      toast.success(`Token sees ${data.count} ad account(s) · app=${data.token_app_name || "?"}`);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Diagnostic failed");
                    }
                  }}
                  data-testid="fb-debug-btn"
                  className="text-xs text-blue-700 hover:underline"
                  title="Show exactly what Facebook Graph returns for your stored token"
                >
                  What does my token see?
                </button>
                <button onClick={disconnect} data-testid="disconnect-fb" className="text-xs text-red-600 hover:underline">
                  Disconnect
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <input
                type="password"
                value={fbToken}
                onChange={(e) => setFbToken(e.target.value)}
                placeholder="System User access token (long-lived)"
                data-testid="fb-token-input"
                className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-500/40"
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={connectFB}
                  disabled={busy.fb}
                  data-testid="connect-fb-btn"
                  className="bg-blue-600 text-white rounded-md px-3 py-1.5 text-sm font-medium hover:bg-blue-700 disabled:opacity-60 flex items-center gap-2"
                >
                  {busy.fb && <Loader2 className="w-3 h-3 animate-spin" />} Connect Facebook
                </button>
                <a
                  href="https://business.facebook.com/settings/system-users"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] text-slate-500 hover:text-slate-900 flex items-center gap-1"
                >
                  Get a System User token <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="text-[11px] text-slate-500 mt-2 leading-relaxed">
                Required permissions: <span className="text-slate-600 font-mono">ads_management</span>, <span className="text-slate-600 font-mono">ads_read</span>, <span className="text-slate-600 font-mono">business_management</span>
              </div>
            </div>
          )}
        </div>

        {/* System1 read-only note (workspace-managed) */}
        <div className="bg-white border border-slate-200 rounded-md p-5 opacity-90">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-cyan-600/20 border border-cyan-500/30 rounded-md flex items-center justify-center">
                <Database className="w-4 h-4 text-cyan-400" />
              </div>
              <div>
                <div className="text-sm font-medium flex items-center gap-2">
                  System1 RSOC
                  <span
                    data-testid="scope-s1"
                    title="Set on the Team page — same key for every member of this workspace"
                    className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border bg-indigo-50 text-indigo-700 border-indigo-200"
                  >
                    <Users className="w-2.5 h-2.5" />
                    Workspace-wide
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 font-mono">Set once by Admin on the Team page · shared by the whole workspace</div>
              </div>
            </div>
            <StatusPill ok={status.system1} label="system1" loading={!statusLoaded || !!statusError} />
          </div>
          {!statusLoaded ? (
            <div className="flex items-center gap-2 text-xs text-slate-500 py-1" data-testid="s1-status-loading">
              <Loader2 className="w-3 h-3 animate-spin" /> Checking System1 workspace keys…
            </div>
          ) : statusError && !status.system1 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-[12px] text-amber-800" data-testid="s1-status-error">
              Couldn't verify S1 status: {statusError}. Keys may still be saved — try the Retry above.
            </div>
          ) : status.system1 ? (
            <div data-testid="s1-connected" className="space-y-3">
              <div className="text-xs text-slate-500">
                Your workspace's partner ID: <span className="font-mono text-slate-900">{status.s1paid || "(not set — add on Team page)"}</span>. Revenue automatically merges into your FB campaigns.
              </div>
              <div className="flex items-center gap-3 text-xs">
                <Link to="/team" data-testid="s1-edit" className="text-blue-600 hover:underline font-medium inline-flex items-center gap-1">
                  Edit keys <ExternalLink className="w-3 h-3" />
                </Link>
                {isAdmin && (
                  <button
                    data-testid="s1-disconnect"
                    disabled={!!rowBusy.s1}
                    onClick={async () => {
                      if (!confirm("Disconnect System1? This clears the auth_key + s1paid for the WHOLE workspace — every buyer on your team will lose S1 revenue data until an admin re-enters them.")) return;
                      setRowBusy((b) => ({ ...b, s1: true }));
                      try {
                        await api.post("/workspace/keys", { s1_auth_key: "", s1paid: "" });
                        try { sessionStorage.removeItem("rsoc.connStatus"); } catch {}
                        toast.success("System1 disconnected for workspace");
                        load();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Disconnect failed");
                      } finally { setRowBusy((b) => ({ ...b, s1: false })); }
                    }}
                    className="text-red-600 hover:underline font-medium inline-flex items-center gap-1 disabled:opacity-50"
                  >
                    {rowBusy.s1 && <Loader2 className="w-3 h-3 animate-spin" />} Disconnect
                  </button>
                )}
              </div>
            </div>
          ) : isAdmin ? (
            <div className="text-xs text-slate-600 space-y-2" data-testid="s1-admin-cta">
              <div>System1 isn't configured yet — you're the admin, so set it up here:</div>
              <Link to="/team" className="inline-flex items-center gap-1 text-[11px] font-medium px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded" data-testid="go-to-team-keys">
                Go to Team → Keys <ExternalLink className="w-3 h-3" />
              </Link>
              <div className="text-[11px] text-slate-500">You'll need the <code>auth_key</code> and <code>s1paid</code> (partner ID) from your System1 account manager.</div>
            </div>
          ) : (
            <div className="text-xs text-slate-500" data-testid="s1-buyer-waiting">
              Your admin hasn't configured System1 yet. Ask them to set the <code className="text-slate-600">auth_key</code> and <code className="text-slate-600">s1paid</code> on the <b>Team</b> page.
            </div>
          )}
        </div>

        {/* FB Pixel + CAPI postback bridge */}
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-amber-500/20 border border-amber-500/30 rounded-md flex items-center justify-center">
                <Zap className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <div className="text-sm font-medium flex items-center gap-1.5">FB CAPI Postback Bridge <ScopeBadge scope="user" testid="scope-pixel" /></div>
                <div className="text-[11px] text-slate-500 font-mono">Forwards S1 revenue → Meta Conversions API · 15-30% optimisation lift</div>
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500 mb-3 leading-relaxed">
            Add your Facebook Pixel ID + Conversions API access token so real revenue events fire back to Meta when S1 reports conversions.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
            <input
              value={pixel.pixel_id}
              onChange={(e) => setPixel({ ...pixel, pixel_id: e.target.value })}
              placeholder="Pixel ID (e.g. 456789012345)"
              data-testid="pixel-id-input"
              className="bg-white border border-slate-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-amber-500/40"
            />
            <input
              value={pixel.test_event_code}
              onChange={(e) => setPixel({ ...pixel, test_event_code: e.target.value })}
              placeholder="Test event code (optional)"
              data-testid="pixel-test-input"
              className="bg-white border border-slate-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-amber-500/40"
            />
          </div>
          <input
            type="password"
            value={pixel.capi_token}
            onChange={(e) => setPixel({ ...pixel, capi_token: e.target.value })}
            placeholder="CAPI access token (Events Manager → Settings → Conversions API → Generate)"
            data-testid="capi-token-input"
            className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm font-mono mb-2 focus:outline-none focus:ring-1 focus:ring-amber-500/40"
          />
          <div className="flex items-center gap-2 mb-4">
            <button onClick={savePixel} disabled={busy.pixel} data-testid="save-pixel-btn" className="bg-blue-600 text-white rounded-md px-3 py-1.5 text-sm font-medium hover:bg-blue-700 disabled:opacity-60 flex items-center gap-2">
              {busy.pixel && <Loader2 className="w-3 h-3 animate-spin" />} Save Pixel + CAPI
            </button>
            <a href="https://business.facebook.com/events_manager2/list/pixel" target="_blank" rel="noreferrer" className="text-[11px] text-slate-500 hover:text-slate-900 flex items-center gap-1">
              Open Events Manager <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="bg-white border border-slate-200 rounded p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">Your postback URL — paste into S1's <code className="text-slate-900">rev_click_track_url</code></div>
            <code className="text-[11px] text-amber-700 font-mono break-all" data-testid="postback-url">{postbackUrl || "loading…"}</code>
            <button onClick={() => { navigator.clipboard.writeText(postbackUrl); toast.success("Copied"); }} className="ml-2 text-[10px] text-slate-500 hover:text-slate-900">copy</button>
          </div>

          {/* Postback health last 24h */}
          <PostbackHealthCard data={postbackHealth} onRefresh={load} />
        </div>

        {/* Emergent Universal LLM Key — admin only */}
        {isAdmin && (
          <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="emergent-key-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-blue-500/20 border border-blue-500/30 rounded-md flex items-center justify-center">
                  <KeyRound className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <div className="text-sm font-medium flex items-center gap-2">
                    Emergent Universal LLM Key
                    <ScopeBadge scope="server" testid="scope-emergent" />
                    <span
                      className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${
                        keysStatus.emergent_llm_set
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-slate-50 text-slate-500 border-slate-200"
                      }`}
                      data-testid="emergent-status-pill"
                    >
                      {keysStatus.emergent_llm_set ? "configured" : "not configured"}
                    </span>
                    {renderTestBtn("emergent", keysStatus.emergent_llm_set)}
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono">
                    Single key for Claude Sonnet 4.5 · Gemini 3 · GPT-Image 1 · Sora 2
                  </div>
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-3 leading-relaxed">
              Powers Campaign Builder briefs, AI insights, Ad Copy, ASC recommendations, image generation,
              and the Sora video tier (when no direct OpenAI key is set). Get yours from Emergent → Profile → Universal Key.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="password"
                value={emergentKey}
                onChange={(e) => setEmergentKey(e.target.value)}
                placeholder="sk-emergent-…"
                data-testid="emergent-key-input"
                className="flex-1 bg-white border border-slate-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-500/40"
              />
              <button
                onClick={saveEmergentKey}
                disabled={busy.emergent}
                data-testid="save-emergent-key-btn"
                className="bg-blue-600 text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-60 flex items-center gap-2"
              >
                {busy.emergent && <Loader2 className="w-3 h-3 animate-spin" />} Save key
              </button>
            </div>
          </div>
        )}

        {/* Resend (transactional email) — admin only */}
        {isAdmin && (
          <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="resend-key-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-pink-500/20 border border-pink-500/30 rounded-md flex items-center justify-center">
                  <Mail className="w-4 h-4 text-pink-600" />
                </div>
                <div>
                  <div className="text-sm font-medium flex items-center gap-2">
                    Resend API key
                    <ScopeBadge scope="server" testid="scope-resend" />
                    <span
                      className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${
                        keysStatus.resend_set
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-slate-50 text-slate-500 border-slate-200"
                      }`}
                      data-testid="resend-status-pill"
                    >
                      {keysStatus.resend_set ? "configured" : "not configured"}
                    </span>
                    {renderTestBtn("resend", keysStatus.resend_set)}
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono">
                    Nightly digest · password reset · invites · CAPI alert emails
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="password"
                value={resendKey}
                onChange={(e) => setResendKey(e.target.value)}
                placeholder="re_…"
                data-testid="resend-key-input"
                className="flex-1 bg-white border border-slate-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-pink-500/40"
              />
              <button
                onClick={saveResendKey}
                disabled={busy.resend}
                data-testid="save-resend-key-btn"
                className="bg-pink-600 text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-pink-700 disabled:opacity-60 flex items-center gap-2"
              >
                {busy.resend && <Loader2 className="w-3 h-3 animate-spin" />} Save key
              </button>
              <a
                href="https://resend.com/api-keys"
                target="_blank"
                rel="noreferrer"
                className="text-[11px] text-slate-500 hover:text-slate-900 flex items-center gap-1"
              >
                Get a key <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}

        {/* AI Video — OpenAI Sora 2 (admin only) */}
        {isAdmin && (
          <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="openai-sora-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-indigo-500/20 border border-indigo-500/30 rounded-md flex items-center justify-center">
                  <Zap className="w-4 h-4 text-indigo-600" />
                </div>
                <div>
                  <div className="text-sm font-medium flex items-center gap-2">
                    AI Video — OpenAI Sora 2 (Tier 1)
                    <ScopeBadge scope="server" testid="scope-openai" />
                    <span
                      className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${
                        soraSource === "direct_openai"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : soraSource === "emergent"
                          ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-slate-50 text-slate-500 border-slate-200"
                      }`}
                      data-testid="sora-status-pill"
                    >
                      {soraSource === "direct_openai"
                        ? "direct · enabled"
                        : soraSource === "emergent"
                        ? "emergent · degraded"
                        : "not configured"}
                    </span>
                    {renderTestBtn("openai", soraSource === "direct_openai")}
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono">
                    Cinematic b-roll · 720x1280 9:16 · 4 / 8 / 12s · ~$0.10/sec
                  </div>
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-3 leading-relaxed">
              Paste your OpenAI API key to route Sora 2 renders directly through OpenAI (bypasses the Emergent proxy).
              Recommended while the Emergent proxy's upstream Sora key is being repaired.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="password"
                value={openaiKey}
                onChange={(e) => setOpenaiKey(e.target.value)}
                placeholder="OPENAI_API_KEY (sk-proj-… or sk-…)"
                data-testid="openai-key-input"
                className="flex-1 bg-white border border-slate-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500/40"
              />
              <button
                onClick={saveOpenaiKey}
                disabled={busy.openai}
                data-testid="save-openai-key-btn"
                className="bg-blue-600 text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-60 flex items-center gap-2"
              >
                {busy.openai && <Loader2 className="w-3 h-3 animate-spin" />} Save key
              </button>
              <a
                href="https://platform.openai.com/account/api-keys"
                target="_blank"
                rel="noreferrer"
                className="text-[11px] text-slate-500 hover:text-slate-900 flex items-center gap-1"
              >
                Get a key <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}

        {/* AI Video (fal.ai) — admin only */}
        {isAdmin && (
          <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="fal-video-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-violet-500/20 border border-violet-500/30 rounded-md flex items-center justify-center">
                  <Zap className="w-4 h-4 text-violet-600" />
                </div>
                <div>
                  <div className="text-sm font-medium flex items-center gap-2">
                    AI Video — fal.ai (UGC Tier 3)
                    <ScopeBadge scope="server" testid="scope-fal" />
                    <span
                      className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${
                        falEnabled
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-slate-50 text-slate-500 border-slate-200"
                      }`}
                      data-testid="fal-status-pill"
                    >
                      {falEnabled ? "enabled" : "not configured"}
                    </span>
                    {renderTestBtn("fal", falEnabled)}
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono">
                    Selfie-style UGC videos for FB Reels · veo3/fast · ~$0.05/sec · 9:16
                  </div>
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-3 leading-relaxed">
              Paste your fal.ai API key to unlock the UGC video tier inside Campaign Builder. Sora 2 (Tier 1) already uses your Emergent Universal Key — no extra config needed.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="password"
                value={falKey}
                onChange={(e) => setFalKey(e.target.value)}
                placeholder="FAL_KEY (e.g. 5a…:b9f3…)"
                data-testid="fal-key-input"
                className="flex-1 bg-white border border-slate-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-violet-500/40"
              />
              <button
                onClick={saveFalKey}
                disabled={busy.fal}
                data-testid="save-fal-key-btn"
                className="bg-blue-600 text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-60 flex items-center gap-2"
              >
                {busy.fal && <Loader2 className="w-3 h-3 animate-spin" />} Save key
              </button>
              <a
                href="https://fal.ai/dashboard/keys"
                target="_blank"
                rel="noreferrer"
                className="text-[11px] text-slate-500 hover:text-slate-900 flex items-center gap-1"
              >
                Get a key <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}

        {/* AI Video — ElevenLabs voiceover (admin only) */}
        {isAdmin && (
          <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="elevenlabs-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-fuchsia-500/20 border border-fuchsia-500/30 rounded-md flex items-center justify-center">
                  <Zap className="w-4 h-4 text-fuchsia-600" />
                </div>
                <div>
                  <div className="text-sm font-medium flex items-center gap-2">
                    AI Voiceover — ElevenLabs (Tier 4)
                    <ScopeBadge scope="server" testid="scope-elevenlabs" />
                    <span
                      className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${
                        voEnabled
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-slate-50 text-slate-500 border-slate-200"
                      }`}
                      data-testid="voiceover-status-pill"
                    >
                      {voEnabled ? "enabled" : "not configured"}
                    </span>
                    {renderTestBtn("elevenlabs", voEnabled)}
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono">
                    Narrate Sora/fal/uploaded clips · 8 preset voices · billed by ElevenLabs
                  </div>
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-3 leading-relaxed">
              Paste your ElevenLabs key to unlock the voiceover pipeline in Campaign Builder. Scripts up to 400 chars get synthesized in &lt;2s and ffmpeg-muxed onto any source mp4.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="password"
                value={elevenKey}
                onChange={(e) => setElevenKey(e.target.value)}
                placeholder="ELEVENLABS_API_KEY (sk_...)"
                data-testid="elevenlabs-key-input"
                className="flex-1 bg-white border border-slate-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-fuchsia-500/40"
              />
              <button
                onClick={saveElevenKey}
                disabled={busy.eleven}
                data-testid="save-elevenlabs-key-btn"
                className="bg-blue-600 text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-60 flex items-center gap-2"
              >
                {busy.eleven && <Loader2 className="w-3 h-3 animate-spin" />} Save key
              </button>
              <a
                href="https://elevenlabs.io/app/settings/api-keys"
                target="_blank"
                rel="noreferrer"
                className="text-[11px] text-slate-500 hover:text-slate-900 flex items-center gap-1"
              >
                Get a key <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}

        {/* Webhook alerts (admin only) */}
        {isAdmin && <WebhookAlertsCard />}

        {/* Launch defaults for Auto-launch (admin only) */}
        {isAdmin && <LaunchDefaultsCard adAccounts={status.fb_ad_accounts || []} />}
      </div>
    </div>
  );
}

// ---------- Preflight wizard: ping every configured integration in one shot ----------
function PreflightWizard({ status, isAdmin }) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState({}); // { provider: { ok, latency_ms, detail, error, hint } }
  const [running, setRunning] = useState(false);
  const [keysStatus, setKeysStatus] = useState({});

  useEffect(() => {
    if (!open) return;
    // Load /llm/health so we know which admin keys are actually configured
    api.get("/llm/health").then(({ data }) => setKeysStatus(data?.keys || {})).catch(() => {});
    setResults({});
  }, [open]);

  const providers = [
    { key: "facebook", label: "Facebook Ads", needed: status.facebook, adminOnly: false, custom: "fb" },
    { key: "s1",       label: "System1 RSOC",  needed: status.system1,  adminOnly: false },
    { key: "emergent", label: "Emergent LLM",  needed: keysStatus?.emergent?.set,     adminOnly: true },
    { key: "openai",   label: "OpenAI (Sora)", needed: keysStatus?.openai?.set,       adminOnly: true },
    { key: "fal",      label: "fal.ai UGC",    needed: keysStatus?.fal?.set,          adminOnly: true },
    { key: "elevenlabs", label: "ElevenLabs",  needed: keysStatus?.elevenlabs?.set,   adminOnly: true },
    { key: "resend",   label: "Resend email",  needed: keysStatus?.resend?.set,       adminOnly: true },
  ].filter((p) => p.needed && (!p.adminOnly || isAdmin));

  const pingOne = async (p) => {
    setResults((r) => ({ ...r, [p.key]: { running: true } }));
    try {
      if (p.custom === "fb") {
        const t0 = performance.now();
        const { data } = await api.get("/connections/facebook/debug");
        const ms = Math.round(performance.now() - t0);
        const activeAccts = (data.ad_accounts || []).filter((a) => a.status === 1).length;
        const totalAccts = (data.ad_accounts || []).length;
        const ok = data.valid && activeAccts > 0;
        const detail = ok
          ? `${activeAccts}/${totalAccts} active ad accounts · app=${data.token_app_name || "?"}`
          : (totalAccts === 0 ? "Token has no ad accounts" : `${totalAccts} account(s) but none Active — all are disabled/closed`);
        setResults((r) => ({ ...r, [p.key]: { ok, latency_ms: ms, detail, error: ok ? null : detail, hint: data.hint } }));
      } else {
        const { data } = await api.post("/connections/test-key", { provider: p.key });
        setResults((r) => ({ ...r, [p.key]: data }));
      }
    } catch (e) {
      setResults((r) => ({ ...r, [p.key]: { ok: false, error: e?.response?.data?.detail || String(e).slice(0, 140) } }));
    }
  };

  const runAll = async () => {
    setRunning(true);
    setResults({});
    // Sequential so we don't rate-limit anyone (and failures are easier to read)
    for (const p of providers) {
      await pingOne(p);
    }
    setRunning(false);
  };

  const passed = Object.values(results).filter((r) => r?.ok).length;
  const failed = Object.values(results).filter((r) => r && r.ok === false).length;
  const total = providers.length;
  const allDone = passed + failed === total && total > 0;
  const allGreen = allDone && failed === 0;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="preflight-open-btn"
        className="flex items-center gap-1.5 bg-gradient-to-br from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white text-xs font-medium rounded-md px-3 py-2 shadow-sm transition-all shrink-0"
        title="Run a one-click health check on every integration"
      >
        <Rocket className="w-3.5 h-3.5" />
        Pre-flight check
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !running && setOpen(false)}
          data-testid="preflight-modal"
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-[580px] max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Rocket className="w-4 h-4 text-indigo-600" />
                  <h2 className="text-lg font-semibold tracking-tight">Go-Live pre-flight</h2>
                </div>
                <p className="text-[11px] text-slate-500 font-mono mt-0.5">
                  Pings every connected integration · measures latency · catches dead keys before launch
                </p>
              </div>
              <button
                onClick={() => !running && setOpen(false)}
                disabled={running}
                data-testid="preflight-close-btn"
                className="text-slate-400 hover:text-slate-900 disabled:opacity-40"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
              {providers.length === 0 && (
                <div className="text-center text-sm text-slate-500 font-mono py-12">
                  No integrations configured yet. Save at least one key and come back.
                </div>
              )}
              {providers.map((p) => {
                const r = results[p.key];
                const state = r?.running ? "running" : (r?.ok === true ? "ok" : r?.ok === false ? "fail" : "idle");
                return (
                  <div
                    key={p.key}
                    data-testid={`preflight-row-${p.key}`}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-md border ${
                      state === "ok"   ? "bg-emerald-50/60 border-emerald-200" :
                      state === "fail" ? "bg-red-50/60 border-red-200" :
                      state === "running" ? "bg-blue-50/60 border-blue-200" :
                      "bg-slate-50 border-slate-200"
                    }`}
                  >
                    <div className="w-6 shrink-0 flex justify-center">
                      {state === "running" && <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />}
                      {state === "ok" && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                      {state === "fail" && <XCircle className="w-4 h-4 text-red-600" />}
                      {state === "idle" && <div className="w-2 h-2 bg-slate-300 rounded-full" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-slate-900">{p.label}</div>
                      {r?.detail && state === "ok" && (
                        <div className="text-[11px] text-emerald-700 font-mono truncate" title={r.detail}>
                          {r.detail}
                        </div>
                      )}
                      {(r?.error || r?.hint) && state === "fail" && (
                        <div className="text-[11px] text-red-700 font-mono truncate" title={`${r.error || ""} ${r.hint || ""}`.trim()}>
                          {(r.error || "").slice(0, 80)}
                          {r.hint && <span className="text-slate-500"> · {r.hint.slice(0, 60)}</span>}
                        </div>
                      )}
                    </div>
                    {state === "ok" && (
                      <div
                        className="text-[10px] font-mono text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5 border border-emerald-200 shrink-0"
                        data-testid={`preflight-latency-${p.key}`}
                      >
                        {r.latency_ms}ms
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-2">
              {allDone ? (
                <div
                  className={`text-xs font-mono font-medium ${allGreen ? "text-emerald-700" : "text-red-700"}`}
                  data-testid="preflight-summary"
                >
                  {allGreen ? `✓ ${passed}/${total} integrations live` : `⚠ ${failed} failed · ${passed} ok`}
                </div>
              ) : (
                <div className="text-xs font-mono text-slate-500">
                  {running ? `Checking ${passed + failed + 1}/${total}…` : `${providers.length} integration(s) ready to test`}
                </div>
              )}
              <div className="flex items-center gap-2">
                {allGreen && (
                  <a
                    href="/optimize"
                    data-testid="preflight-arm-breaker-cta"
                    className="text-xs text-emerald-700 hover:underline flex items-center gap-1"
                    title="You're green — now arm the Circuit Breaker to protect spend"
                  >
                    <Zap className="w-3 h-3" /> Arm Circuit Breaker →
                  </a>
                )}
                <button
                  onClick={runAll}
                  disabled={running || providers.length === 0}
                  data-testid="preflight-run-btn"
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-medium rounded-md px-4 py-1.5 flex items-center gap-1.5"
                >
                  {running ? <><Loader2 className="w-3 h-3 animate-spin" /> Running…</> : (allDone ? "Re-run" : "Run all")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}



function PostbackHealthCard({ data, onRefresh }) {
  if (!data) return null;
  const healthStyles = {
    healthy:       { dot: "bg-emerald-500", label: "Healthy",       labelBg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    degraded:      { dot: "bg-amber-500",   label: "Degraded",      labelBg: "bg-amber-50 text-amber-700 border-amber-200" },
    failing:       { dot: "bg-red-500",     label: "Failing",       labelBg: "bg-red-50 text-red-700 border-red-200" },
    setup_needed:  { dot: "bg-blue-500",    label: "Setup needed",  labelBg: "bg-blue-50 text-blue-700 border-blue-200" },
    idle:          { dot: "bg-slate-400",   label: "Idle",          labelBg: "bg-slate-50 text-slate-500 border-slate-200" },
  };
  const s = healthStyles[data.health] || healthStyles.idle;
  const statusTag = (st) => {
    if (st === "forwarded") return "text-emerald-700 bg-emerald-50 border-emerald-200";
    if (st === "skipped")   return "text-amber-700 bg-amber-50 border-amber-200";
    return "text-red-700 bg-red-50 border-red-200"; // errored
  };

  return (
    <div className="mt-3 bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded p-3" data-testid="postback-health-card">
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <Activity className="w-3.5 h-3.5 text-slate-500" />
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Postback health · last 24h</span>
          <span className={`flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border ${s.labelBg}`} data-testid="postback-health-status">
            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`}></span>
            {s.label}
          </span>
        </div>
        <button
          onClick={onRefresh}
          data-testid="postback-refresh"
          className="text-[10px] font-mono text-slate-500 hover:text-slate-900"
        >
          refresh
        </button>
      </div>

      <div className="grid grid-cols-4 gap-2 mb-3">
        <div className="bg-white border border-slate-200 rounded p-2 text-center">
          <div className="text-[10px] text-slate-500 font-mono">received</div>
          <div className="text-lg font-semibold text-slate-900" data-testid="postback-received">{data.total}</div>
        </div>
        <div className="bg-white border border-emerald-200 rounded p-2 text-center">
          <div className="text-[10px] text-emerald-700 font-mono">forwarded</div>
          <div className="text-lg font-semibold text-emerald-700" data-testid="postback-forwarded">{data.forwarded}</div>
        </div>
        <div className="bg-white border border-amber-200 rounded p-2 text-center">
          <div className="text-[10px] text-amber-700 font-mono">skipped</div>
          <div className="text-lg font-semibold text-amber-700" data-testid="postback-skipped">{data.skipped}</div>
        </div>
        <div className="bg-white border border-red-200 rounded p-2 text-center">
          <div className="text-[10px] text-red-700 font-mono">errored</div>
          <div className="text-lg font-semibold text-red-700" data-testid="postback-errored">{data.errored}</div>
        </div>
      </div>

      {data.forwarded > 0 && (
        <div className="text-[11px] font-mono text-slate-600 mb-2">
          <span className="text-slate-900 font-semibold">${data.forwarded_value_usd.toLocaleString()}</span> in revenue signal sent to Meta CAPI
          {data.last_event_at && <span className="text-slate-400"> · last at {new Date(data.last_event_at).toLocaleTimeString()}</span>}
        </div>
      )}

      {/* Event Match Quality (EMQ) */}
      {data.emq && (
        <div data-testid="postback-emq" className="flex items-center gap-2 bg-white border border-slate-200 rounded px-2 py-1.5 mb-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">EMQ</span>
          {data.emq.avg !== null ? (
            <>
              <span
                data-testid="postback-emq-score"
                className={`text-sm font-semibold ${
                  data.emq.status === "excellent" ? "text-emerald-700" :
                  data.emq.status === "good" ? "text-emerald-600" :
                  data.emq.status === "fair" ? "text-amber-600" :
                  "text-red-600"
                }`}
              >
                {data.emq.avg.toFixed(1)}
                <span className="text-[10px] text-slate-400 ml-0.5">/10</span>
              </span>
              <span
                className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                  data.emq.status === "excellent" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                  data.emq.status === "good" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                  data.emq.status === "fair" ? "bg-amber-50 text-amber-700 border-amber-200" :
                  "bg-red-50 text-red-700 border-red-200"
                }`}
                data-testid="postback-emq-status"
              >
                {data.emq.status}
              </span>
              <span className="text-[10px] text-slate-500 ml-auto">
                {data.emq.samples} sample{data.emq.samples === 1 ? "" : "s"}
              </span>
            </>
          ) : (
            <span className="text-[11px] font-mono text-slate-400 italic" data-testid="postback-emq-empty">
              no data yet
            </span>
          )}
        </div>
      )}
      {data.emq?.note && data.emq.avg !== null && data.emq.avg < 8.0 && (
        <div className="flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 text-[11px] text-amber-800 mb-2">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>{data.emq.note}</span>
        </div>
      )}

      {data.health === "setup_needed" && (
        <div className="flex items-start gap-1.5 bg-blue-50 border border-blue-200 rounded px-2 py-1.5 text-[11px] text-blue-800 mb-2">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>S1 is sending postbacks but no Pixel/CAPI token is set yet. Save your Pixel ID + CAPI token above to start forwarding to Meta.</span>
        </div>
      )}
      {data.health === "failing" && (
        <div className="flex items-start gap-1.5 bg-red-50 border border-red-200 rounded px-2 py-1.5 text-[11px] text-red-800 mb-2">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>All recent postbacks errored at Meta. Check your CAPI token is current (they expire) and the Pixel ID matches.</span>
        </div>
      )}
      {data.health === "idle" && (
        <div className="text-[11px] text-slate-500 font-mono italic">
          No postbacks received in the last 24h. Paste the URL above into S1's rev_click_track_url.
        </div>
      )}

      {data.recent.length > 0 && (
        <details className="mt-2">
          <summary className="text-[10px] font-mono text-slate-500 hover:text-slate-900 cursor-pointer select-none">Show last 5 events</summary>
          <div className="mt-2 space-y-1" data-testid="postback-recent-list">
            {data.recent.map((e, i) => (
              <div key={`${e.at}-${e.status}-${i}`} className="flex items-center gap-2 text-[11px] font-mono" data-testid={`postback-event-${i}`}>
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${statusTag(e.status)}`}>{e.status}</span>
                <span className="text-slate-500">{new Date(e.at).toLocaleTimeString()}</span>
                <span className="text-emerald-600">${Number(e.value || 0).toFixed(2)}</span>
                <span className="text-slate-400 truncate" title={e.note}>{e.note}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function WebhookAlertsCard() {
  const [cfg, setCfg] = useState({ enabled: false, webhook_url: "", provider: "slack" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/alerts/webhook").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.post("/alerts/webhook", next);
      setCfg({ ...next, provider: data.provider });
      toast.success(next.enabled ? `Alerts ON (${data.provider})` : "Alerts OFF");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await api.post("/alerts/webhook/test");
      toast.success("Test alert sent — check your channel");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="webhook-alerts-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-indigo-500/10 border border-indigo-500/30 rounded-md flex items-center justify-center">
            <Bell className="w-4 h-4 text-indigo-600" />
          </div>
          <div>
            <div className="text-sm font-medium">Slack / Discord alerts</div>
            <div className="text-[11px] text-slate-500 font-mono">Auto-ping when CAPI postback transitions to failing · token expires · etc.</div>
          </div>
        </div>
        <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${cfg.enabled ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"}`}>
          {cfg.enabled ? "ON" : "OFF"}
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-3 leading-relaxed">
        Paste an incoming webhook URL from Slack or Discord. The system watches postback health every 30 min and fires an alert only on transitions (healthy → failing, degraded → recovered) — no spam.
      </p>
      <input
        type="text"
        value={cfg.webhook_url}
        onChange={(e) => setCfg({ ...cfg, webhook_url: e.target.value })}
        placeholder="https://hooks.slack.com/services/T00/B00/xxx   OR   https://discord.com/api/webhooks/..."
        data-testid="webhook-url-input"
        className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm font-mono mb-2 focus:outline-none focus:ring-1 focus:ring-indigo-500/40"
      />
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            checked={cfg.enabled}
            onChange={(e) => save({ ...cfg, enabled: e.target.checked })}
            disabled={saving || !cfg.webhook_url}
            data-testid="webhook-enabled-toggle"
            className="w-4 h-4 accent-indigo-600"
          />
          Enable alerts
        </label>
        <button
          onClick={() => save(cfg)}
          disabled={saving}
          data-testid="webhook-save-btn"
          className="bg-blue-600 text-white rounded-md px-3 py-1.5 text-xs font-medium hover:bg-blue-700 disabled:opacity-60 flex items-center gap-2"
        >
          {saving && <Loader2 className="w-3 h-3 animate-spin" />} Save
        </button>
        <button
          onClick={sendTest}
          disabled={testing || !cfg.enabled || !cfg.webhook_url}
          data-testid="webhook-test-btn"
          className="bg-white border border-slate-300 text-slate-700 rounded-md px-3 py-1.5 text-xs font-medium hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2"
        >
          {testing && <Loader2 className="w-3 h-3 animate-spin" />} Send test
        </button>
        <span className="text-[10px] font-mono text-slate-400 ml-auto">
          provider: <span className="text-slate-700">{cfg.provider || "auto"}</span>
        </span>
      </div>
    </div>
  );
}

function LaunchDefaultsCard({ adAccounts }) {
  const [cfg, setCfg] = useState({ ad_account_id: "", page_id: "", landing_url: "", daily_budget_usd: 20, daily_spend_cap_usd: 0, style: "native", configured: false, today_committed_usd: 0, cap_remaining_usd: null });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/workspace/launch-defaults").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/workspace/launch-defaults", cfg);
      setCfg({ ...cfg, configured: data.configured });
      toast.success(data.configured ? "Launch defaults saved — Auto-launch enabled" : "Launch defaults saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="launch-defaults-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-amber-500 to-red-500 rounded-md flex items-center justify-center">
            <Zap className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-sm font-medium">Auto-launch defaults</div>
            <div className="text-[11px] text-slate-500 font-mono">Powers the zero-click ship button on Scouting cards</div>
          </div>
        </div>
        <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${cfg.configured ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"}`}>
          {cfg.configured ? "READY" : "INCOMPLETE"}
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-3 leading-relaxed">
        When a media buyer clicks the ⚡ Auto-launch button on a Scouting card, we'll use these defaults to create a <strong>paused</strong> Meta campaign in one shot. Campaigns always start paused — flip to Active in Ads Manager after reviewing.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1 block">Ad account</label>
          <select
            value={cfg.ad_account_id}
            onChange={(e) => setCfg({ ...cfg, ad_account_id: e.target.value })}
            data-testid="launch-ad-account"
            className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm font-mono"
          >
            <option value="">— select —</option>
            {adAccounts.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.id}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1 block">Page ID</label>
          <input
            value={cfg.page_id}
            onChange={(e) => setCfg({ ...cfg, page_id: e.target.value })}
            placeholder="e.g. 123456789012345"
            data-testid="launch-page-id"
            className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm font-mono"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1 block">Landing URL (S1 tracking URL)</label>
          <input
            value={cfg.landing_url}
            onChange={(e) => setCfg({ ...cfg, landing_url: e.target.value })}
            placeholder="https://subdomain.your-lp.com/?s1paid=XXX&..."
            data-testid="launch-landing-url"
            className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm font-mono"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1 block">Per-campaign budget</label>
          <input
            type="number" min="5"
            value={cfg.daily_budget_usd}
            onChange={(e) => setCfg({ ...cfg, daily_budget_usd: parseInt(e.target.value) || 0 })}
            data-testid="launch-budget"
            className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm font-mono"
          />
        </div>
      </div>

      {/* Daily spend cap — safety rail for zero-click auto-launch */}
      <div className="bg-red-50 border border-red-200 rounded p-2.5 mb-3">
        <div className="flex items-center gap-2 mb-1.5">
          <Zap className="w-3.5 h-3.5 text-red-600" />
          <span className="text-[10px] uppercase tracking-wider text-red-700 font-mono font-bold">Daily spend cap (auto-launch only)</span>
        </div>
        <p className="text-[11px] text-slate-600 mb-2 leading-relaxed">
          Caps the total daily budget auto-launched via the ⚡ button. If multiple buyers click Auto-launch aggressively, they'll stop at this cap (resets midnight UTC). <strong>Leave at 0 for no cap.</strong>
        </p>
        <div className="flex items-center gap-2">
          <input
            type="number" min="0" step="25"
            value={cfg.daily_spend_cap_usd || 0}
            onChange={(e) => setCfg({ ...cfg, daily_spend_cap_usd: parseInt(e.target.value) || 0 })}
            placeholder="0"
            data-testid="launch-cap"
            className="w-28 bg-white border border-red-300 rounded-md px-3 py-1.5 text-sm font-mono"
          />
          <span className="text-[11px] text-slate-600 font-mono">USD/day · committed today: <span className="text-slate-900 font-semibold">${cfg.today_committed_usd || 0}</span>
            {cfg.daily_spend_cap_usd > 0 && <> · remaining: <span className="text-emerald-700 font-semibold">${cfg.cap_remaining_usd ?? cfg.daily_spend_cap_usd}</span></>}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Ad style</label>
        <select
          value={cfg.style}
          onChange={(e) => setCfg({ ...cfg, style: e.target.value })}
          data-testid="launch-style"
          className="bg-white border border-slate-200 rounded-md px-2 py-1 text-xs font-mono"
        >
          <option value="native">Native Editorial</option>
          <option value="ugly">Ugly Ad (Clickbait)</option>
          <option value="modern">Modern SaaS</option>
        </select>
        <button
          onClick={save}
          disabled={saving}
          data-testid="launch-save"
          className="ml-auto bg-blue-600 text-white rounded-md px-3 py-1.5 text-xs font-medium hover:bg-blue-700 disabled:opacity-60 flex items-center gap-2"
        >
          {saving && <Loader2 className="w-3 h-3 animate-spin" />} Save defaults
        </button>
      </div>
    </div>
  );
}
